/**
 * Historical Records — "Books" tab (Accounting Session 2). Pure UI wrapper
 * around already-built endpoints (GET/POST /api/historical-records, PUT
 * /{id}/fields, PATCH /{id}/status, PUT /{id}/link-trip, DELETE /{id},
 * POST /{id}/allocate-stub, GET /by-ucc/{ucc}, GET /audit-log, and A6/A7's
 * GET /api/allocations/by-source/{type}/{id}) — zero new backend logic.
 * Date-range is the one filter the list endpoint doesn't support server-
 * side, so it's applied client-side on the already-fetched page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  BookOpen, Plus, Search, Eye, Loader2, History, Ban, Link2, Layers, ShieldCheck,
  Pencil, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RECORD_TYPES = ['trip', 'invoice', 'purchase', 'payment', 'receipt', 'vendor_transaction', 'credit_note', 'debit_note'];
const VERIFICATION_STATUSES = [
  'Source Document Available', 'Imported', 'Extracted', 'Matched', 'Human Reviewed',
  'Verified', 'Verified - Under Review', 'Partially Verified', 'Missing Evidence', 'Rejected/Duplicate',
];
const ALLOWED_TRANSITIONS = {
  'Source Document Available': ['Imported', 'Extracted', 'Missing Evidence', 'Rejected/Duplicate'],
  Imported: ['Matched', 'Human Reviewed', 'Missing Evidence', 'Rejected/Duplicate'],
  Extracted: ['Matched', 'Human Reviewed', 'Missing Evidence', 'Rejected/Duplicate'],
  Matched: ['Human Reviewed', 'Partially Verified', 'Missing Evidence', 'Rejected/Duplicate'],
  'Human Reviewed': ['Verified', 'Partially Verified', 'Missing Evidence', 'Rejected/Duplicate'],
  'Partially Verified': ['Human Reviewed', 'Verified', 'Missing Evidence', 'Rejected/Duplicate'],
  Verified: ['Rejected/Duplicate', 'Verified - Under Review'],
  'Verified - Under Review': ['Verified', 'Rejected/Duplicate'],
  'Missing Evidence': ['Source Document Available', 'Imported', 'Rejected/Duplicate'],
  'Rejected/Duplicate': [],
};
const STATUS_TONE = {
  Verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Verified - Under Review': 'bg-amber-50 text-amber-700 border-amber-200',
  'Partially Verified': 'bg-amber-50 text-amber-700 border-amber-200',
  'Rejected/Duplicate': 'bg-rose-50 text-rose-700 border-rose-200',
  'Missing Evidence': 'bg-rose-50 text-rose-700 border-rose-200',
};
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

// ---------------------------------------------------------------------------
const CreateRecordDialog = ({ onClose, onCreated }) => {
  const [recordType, setRecordType] = useState('invoice');
  const [originalNumber, setOriginalNumber] = useState('');
  const [originalDate, setOriginalDate] = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [linkedUCC, setLinkedUCC] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldRows, setFieldRows] = useState([{ name: 'amount', value: '' }]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!originalDate && !financialYear) { toast.error('Provide an original date or a financial year'); return; }
    const fields = {};
    fieldRows.forEach((r) => { if (r.name.trim()) fields[r.name.trim()] = { value: r.value, source: 'human_entered' }; });
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/historical-records`, {
        recordType, originalNumber: originalNumber || null, originalDate: originalDate || null,
        financialYear: financialYear || null, importSource: 'manual_entry',
        linkedUCC: linkedUCC.trim() || null, fields, notes,
      });
      toast.success(`${res.data.crmDisplayNumber} created`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="create-record-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New historical record</DialogTitle>
          <DialogDescription>Manual entry — reuses the same POST /api/historical-records endpoint as everything else.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label>Record type</Label>
            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger data-testid="create-record-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RECORD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Original number</Label><Input data-testid="create-original-number-input" value={originalNumber} onChange={(e) => setOriginalNumber(e.target.value)} /></div>
            <div><Label>Original date</Label><Input type="date" data-testid="create-original-date-input" value={originalDate} onChange={(e) => setOriginalDate(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Financial year (if no date)</Label><Input placeholder="e.g. 2023-24" data-testid="create-fy-input" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} /></div>
            <div><Label>Linked UCC</Label><Input placeholder="e.g. UC1094" data-testid="create-ucc-input" value={linkedUCC} onChange={(e) => setLinkedUCC(e.target.value)} /></div>
          </div>
          <div>
            <Label>Fields</Label>
            <div className="space-y-2">
              {fieldRows.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`create-field-row-${i}`}>
                  <Input placeholder="Field name" value={row.name} onChange={(e) => setFieldRows((rs) => rs.map((r, j) => j === i ? { ...r, name: e.target.value } : r))} />
                  <Input placeholder="Value" value={row.value} onChange={(e) => setFieldRows((rs) => rs.map((r, j) => j === i ? { ...r, value: e.target.value } : r))} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFieldRows((rs) => rs.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" data-testid="add-create-field-row-btn" onClick={() => setFieldRows((rs) => [...rs, { name: '', value: '' }])}>+ Add field</Button>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="create-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-record-submit-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
const VoidDialog = ({ record, onClose, onVoided }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error('A reason is required to void a record'); return; }
    setSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/historical-records/${record.historicalId}`, { data: { reason } });
      toast.success('Record voided');
      onVoided();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void record');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="void-record-dialog">
        <DialogHeader>
          <DialogTitle>Void this record</DialogTitle>
          <DialogDescription>Soft-delete only — the record stays fully traceable, never removed. This cannot be undone once saved.</DialogDescription>
        </DialogHeader>
        <div><Label>Reason (required)</Label><Textarea data-testid="void-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="void-record-submit-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Void record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
const RecordDetailSheet = ({ historicalId, onClose, onChanged }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fieldEdits, setFieldEdits] = useState({});
  const [savingFields, setSavingFields] = useState(false);
  const [statusTarget, setStatusTarget] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [chain, setChain] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [allocRef, setAllocRef] = useState(null);
  const [loadingAlloc, setLoadingAlloc] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [linkTripId, setLinkTripId] = useState('');
  const [tripCandidates, setTripCandidates] = useState([]);
  const [savingLink, setSavingLink] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/historical-records/${historicalId}`)
      .then((r) => { setRecord(r.data); setFieldEdits({}); })
      .catch(() => toast.error('Failed to load record'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [historicalId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!record?.linkedUCC) { setChain(null); return; }
    axios.get(`${BACKEND_URL}/api/historical-records/by-ucc/${encodeURIComponent(record.linkedUCC)}`)
      .then((r) => setChain(r.data))
      .catch(() => setChain(null));
  }, [record?.linkedUCC]);

  useEffect(() => {
    if (record?.linkedUCC && record.recordType !== 'trip' && !record.linkedHistoricalTripId) {
      axios.get(`${BACKEND_URL}/api/historical-records`, { params: { recordType: 'trip', linkedUCC: record.linkedUCC } })
        .then((r) => setTripCandidates(r.data || []))
        .catch(() => setTripCandidates([]));
    } else {
      setTripCandidates([]);
    }
  }, [record?.linkedUCC, record?.recordType, record?.linkedHistoricalTripId]);

  const toggleAudit = () => {
    if (showAudit) { setShowAudit(false); return; }
    setShowAudit(true);
    if (!auditLog) {
      axios.get(`${BACKEND_URL}/api/historical-records/audit-log`, { params: { historicalId } })
        .then((r) => setAuditLog(r.data || []))
        .catch(() => setAuditLog([]));
    }
  };

  const loadAllocationRef = () => {
    setLoadingAlloc(true);
    axios.get(`${BACKEND_URL}/api/allocations/by-source/historical_record/${historicalId}`)
      .then((r) => setAllocRef(r.data))
      .catch(() => setAllocRef({ error: true }))
      .finally(() => setLoadingAlloc(false));
  };

  const saveFieldEdits = async () => {
    const updates = {};
    Object.entries(fieldEdits).forEach(([k, v]) => { updates[k] = { value: v, source: 'human_entered' }; });
    if (Object.keys(updates).length === 0) { toast.error('No field changes to save'); return; }
    setSavingFields(true);
    try {
      await axios.put(`${BACKEND_URL}/api/historical-records/${historicalId}/fields`, {
        fieldUpdates: updates, reason: 'Edited via Books tab',
      });
      toast.success('Fields updated — audit-logged');
      load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save field edits');
    } finally {
      setSavingFields(false);
    }
  };

  const saveStatus = async () => {
    if (!statusTarget) return;
    setSavingStatus(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/historical-records/${historicalId}/status`, { status: statusTarget });
      toast.success(`Status → ${statusTarget}`);
      setStatusTarget('');
      load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change status');
    } finally {
      setSavingStatus(false);
    }
  };

  const saveLinkTrip = async () => {
    if (!linkTripId) return;
    setSavingLink(true);
    try {
      await axios.put(`${BACKEND_URL}/api/historical-records/${historicalId}/link-trip`, { linkedHistoricalTripId: linkTripId });
      toast.success('Linked to trip');
      load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to link trip');
    } finally {
      setSavingLink(false);
    }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="record-detail-sheet">
        {loading || !record ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                {record.crmDisplayNumber}
                <Badge className="bg-slate-100 text-slate-600 text-[10px] uppercase">{record.recordType}</Badge>
                {record.voided && <Badge className="bg-rose-100 text-rose-700 text-[10px] uppercase" data-testid="record-voided-badge">Voided</Badge>}
              </SheetTitle>
              <SheetDescription>
                Original # {record.originalNumber || '—'} · {fmtDate(record.originalDate)} · FY {record.financialYear || '—'}
              </SheetDescription>
            </SheetHeader>

            {/* Status + provenance */}
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Badge className={`border ${STATUS_TONE[record.verificationStatus] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-testid="record-status-badge">
                  {record.verificationStatus}
                </Badge>
                {!record.voided && (ALLOWED_TRANSITIONS[record.verificationStatus] || []).length > 0 && (
                  <div className="flex items-center gap-2">
                    <Select value={statusTarget} onValueChange={setStatusTarget}>
                      <SelectTrigger className="w-48 h-8 text-xs" data-testid="status-target-select"><SelectValue placeholder="Change status to…" /></SelectTrigger>
                      <SelectContent>
                        {(ALLOWED_TRANSITIONS[record.verificationStatus] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" disabled={!statusTarget || savingStatus} onClick={saveStatus} data-testid="status-save-btn">
                      {savingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div>Import source: <span className="font-medium text-slate-700">{record.importSource}</span></div>
                <div>Imported by: <span className="font-medium text-slate-700">{record.importedBy || '—'}</span></div>
                <div>Source document: <span className="font-mono text-[11px]">{record.sourceDocumentId || '—'}</span></div>
                <div>Source line: <span className="font-mono text-[11px]">{record.sourceLineId || '—'}</span></div>
                <div>Linked UCC: <span className="font-medium text-slate-700">{record.linkedUCC || '—'}</span></div>
                <div>Extraction confidence: <span className="font-medium text-slate-700">{record.provenance?.confidence || '—'}</span></div>
              </div>
            </Card>

            {/* Fields */}
            <Card className="p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                <Pencil className="h-3.5 w-3.5" />Fields
              </div>
              <div className="space-y-2">
                {Object.entries(record.fields || {}).map(([name, entry]) => (
                  <div key={name} className="flex items-center gap-2" data-testid={`field-row-${name}`}>
                    <div className="w-28 text-xs text-slate-500 truncate" title={name}>{name}</div>
                    <Input
                      className="h-8 text-sm flex-1"
                      data-testid={`field-input-${name}`}
                      value={fieldEdits[name] !== undefined ? fieldEdits[name] : (entry?.value ?? '')}
                      onChange={(e) => setFieldEdits((f) => ({ ...f, [name]: e.target.value }))}
                    />
                    <span className="text-[10px] text-slate-400 w-24 truncate">{entry?.source || '—'} · {entry?.confidence || '—'}</span>
                  </div>
                ))}
                {Object.keys(record.fields || {}).length === 0 && <p className="text-xs text-slate-400">No fields recorded.</p>}
              </div>
              <Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700 text-white" disabled={savingFields || Object.keys(fieldEdits).length === 0}
                onClick={saveFieldEdits} data-testid="save-field-edits-btn">
                {savingFields ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save field edits'}
              </Button>
            </Card>

            {/* Link-trip chain */}
            <Card className="p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                <Link2 className="h-3.5 w-3.5" />UCC → Trip → Record chain
              </div>
              {!record.linkedUCC ? (
                <p className="text-xs text-slate-400" data-testid="no-ucc-state">No linked UCC on this record — nothing to chain.</p>
              ) : record.recordType === 'trip' ? (
                <p className="text-xs text-slate-400">This is itself a trip record — other records link to it, it doesn't link onward.</p>
              ) : record.linkedHistoricalTripId ? (
                <div className="text-xs text-slate-600" data-testid="linked-trip-summary">
                  Linked to trip: <span className="font-mono">{record.linkedHistoricalTripId}</span>
                  {chain?.byType?.trip?.find((t) => t.historicalId === record.linkedHistoricalTripId) && (
                    <span> — {chain.byType.trip.find((t) => t.historicalId === record.linkedHistoricalTripId).crmDisplayNumber}</span>
                  )}
                  <div className="mt-1 text-[11px] text-slate-400">Locked — a linked trip can be changed to another trip, but never cleared.</div>
                </div>
              ) : tripCandidates.length === 0 ? (
                <p className="text-xs text-slate-400" data-testid="no-trip-candidates">No historical trip record found yet for UCC {record.linkedUCC} — nothing to link to.</p>
              ) : (
                <div className="flex items-center gap-2" data-testid="link-trip-form">
                  <Select value={linkTripId} onValueChange={setLinkTripId}>
                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid="link-trip-select"><SelectValue placeholder="Select a historical trip" /></SelectTrigger>
                    <SelectContent>
                      {tripCandidates.map((t) => <SelectItem key={t.historicalId} value={t.historicalId}>{t.crmDisplayNumber}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" disabled={!linkTripId || savingLink} onClick={saveLinkTrip} data-testid="link-trip-submit-btn">
                    {savingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Link'}
                  </Button>
                </div>
              )}
              {chain && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1" data-testid="ucc-chain-list">
                  {Object.entries(chain.byType || {}).map(([type, recs]) => (
                    <div key={type} className="text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-600 uppercase">{type}</span>: {recs.length} record(s)
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Allocate-stub / allocation reference (read-only) */}
            <Card className="p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" />Allocation reference (read-only)
              </div>
              {record.allocationStub ? (
                <div className="text-xs text-slate-600 mb-2" data-testid="allocation-stub-state">
                  Stub: <span className="font-medium">{record.allocationStub.status}</span> — confirmed by {record.allocationStub.confirmedBy} on {fmtDate(record.allocationStub.confirmedAt)}
                </div>
              ) : (
                <p className="text-xs text-slate-400 mb-2" data-testid="no-allocation-stub">No allocate-stub confirmed on this record yet.</p>
              )}
              {!allocRef ? (
                <Button size="sm" variant="outline" onClick={loadAllocationRef} disabled={loadingAlloc} data-testid="load-allocation-ref-btn">
                  {loadingAlloc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'View real allocation lines (A6/A7)'}
                </Button>
              ) : allocRef.error ? (
                <p className="text-xs text-rose-500">Could not load allocation reference.</p>
              ) : (
                <div className="text-xs text-slate-600 space-y-1" data-testid="allocation-ref-data">
                  <div>Total: {allocRef.sourceTotalAmount ?? '—'} · Allocated: {allocRef.allocatedTotal} · Remainder: {allocRef.unallocatedRemainder ?? '—'}</div>
                  <div className="text-[11px] text-slate-400">{allocRef.lines?.length || 0} allocation line(s) — manage them in Allocations, not here.</div>
                </div>
              )}
            </Card>

            {/* Audit trail */}
            <div>
              <Button variant="ghost" size="sm" onClick={toggleAudit} data-testid="view-record-audit-btn">
                <History className="h-3.5 w-3.5 mr-1" />Audit trail
              </Button>
              {showAudit && (
                <div className="mt-2 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] font-mono max-h-40 overflow-y-auto" data-testid="record-audit-log">
                  {!auditLog ? <div className="text-slate-400">Loading…</div> : auditLog.length === 0 ? (
                    <div className="text-slate-400">No audit entries yet.</div>
                  ) : auditLog.map((e, i) => (
                    <div key={i} className="mb-1">[{e.occurredAt}] {e.attemptedAction} by {e.attemptedBy} — {e.reason}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Void */}
            <div className="pt-2 border-t border-slate-100">
              {record.voided ? (
                <p className="text-xs text-rose-500" data-testid="already-voided-note">
                  Voided by {record.voidedBy} on {fmtDate(record.voidedAt)} — {record.voidReason}
                </p>
              ) : (
                <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setShowVoid(true)} data-testid="open-void-dialog-btn">
                  <Ban className="h-3.5 w-3.5 mr-1" />Void this record
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
      {showVoid && (
        <VoidDialog record={record} onClose={() => setShowVoid(false)} onVoided={() => { setShowVoid(false); load(); onChanged(); }} />
      )}
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
const AccountingBooks = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordType, setRecordType] = useState('all');
  const [verificationStatus, setVerificationStatus] = useState('all');
  const [ucc, setUcc] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (recordType !== 'all') params.recordType = recordType;
    if (verificationStatus !== 'all') params.verificationStatus = verificationStatus;
    if (ucc.trim()) params.linkedUCC = ucc.trim();
    axios.get(`${BACKEND_URL}/api/historical-records`, { params })
      .then((r) => setRecords(r.data || []))
      .catch(() => toast.error('Failed to load historical records'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const d = r.originalDate || r.createdAt;
      const dStr = d ? String(d).slice(0, 10) : null;
      if (dateFrom && (!dStr || dStr < dateFrom)) return false;
      if (dateTo && (!dStr || dStr > dateTo)) return false;
      return true;
    });
  }, [records, dateFrom, dateTo]);

  return (
    <div className="space-y-4" data-testid="accounting-books-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-teal-700" />Historical Records
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Browse, verify and correct reconstructed pre-CRM books — nothing here touches live trips/invoices.</p>
        </div>
        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowCreate(true)} data-testid="new-record-btn">
          <Plus className="h-4 w-4 mr-1" />New record
        </Button>
      </div>

      <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="books-filter-bar">
        <Select value={recordType} onValueChange={setRecordType}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-record-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {RECORD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={verificationStatus} onValueChange={setVerificationStatus}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {VERIFICATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="UCC" className="w-32 h-8 text-xs" value={ucc} onChange={(e) => setUcc(e.target.value)} data-testid="filter-ucc" />
        <Input type="date" className="w-36 h-8 text-xs" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" className="w-36 h-8 text-xs" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
        <Button size="sm" variant="outline" onClick={load} data-testid="apply-filters-btn"><Search className="h-3.5 w-3.5 mr-1" />Apply</Button>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="books-loading"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="books-empty-state">
          <ShieldCheck className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">No historical records match these filters.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="books-list">
          {filtered.map((r) => (
            <Card key={r.historicalId} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`book-row-${r.historicalId}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Badge className="bg-slate-100 text-slate-600 text-[10px] uppercase shrink-0">{r.recordType}</Badge>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{r.crmDisplayNumber}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {r.originalNumber || '—'} · {fmtDate(r.originalDate)} · {r.financialYear || '—'} · {r.linkedUCC || 'no UCC'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.voided && <Badge className="bg-rose-100 text-rose-700 text-[10px]">Voided</Badge>}
                <Badge className={`border text-[10px] ${STATUS_TONE[r.verificationStatus] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{r.verificationStatus}</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedId(r.historicalId)} data-testid={`view-record-${r.historicalId}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" />View
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateRecordDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {selectedId && (
        <RecordDetailSheet historicalId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
      )}
    </div>
  );
};

export default AccountingBooks;
